/**
 * FOLLOWER BOOST — premium dropdown (progressive enhancement of <select.fb-select>)
 *
 * The native <select> stays in the DOM as the source of truth (forms, no-JS,
 * assistive tech fallback) and is visually hidden. A button[role=combobox] +
 * ul[role=listbox] replaces it: keyboard (arrows, Home/End, type-ahead, Enter,
 * Escape), click outside, focus visible, ARIA, touch-friendly targets.
 * When the native options change (quick configurator refills packs) the list
 * is rebuilt via MutationObserver and `change` keeps firing on the <select>.
 */

let uid = 0;

class FbSelect {
  /** @param {HTMLSelectElement} select */
  constructor(select) {
    this.select = select;
    this.id = `fb-dd-${++uid}`;
    this.open = false;

    this.wrap = document.createElement('div');
    this.wrap.className = 'fb-dd' + (select.classList.contains('fb-select--sm') ? ' fb-dd--sm' : '');
    select.parentNode.insertBefore(this.wrap, select);
    this.wrap.appendChild(select);

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'fb-dd__btn';
    this.button.setAttribute('role', 'combobox');
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', `${this.id}-list`);
    const label = select.getAttribute('aria-label') || this.findLabel();
    if (label) this.button.setAttribute('aria-label', label);
    this.button.innerHTML = '<span class="fb-dd__value"></span><i class="fb-dd__chevron" aria-hidden="true"></i>';
    this.value = this.button.querySelector('.fb-dd__value');

    this.list = document.createElement('ul');
    this.list.className = 'fb-dd__list';
    this.list.id = `${this.id}-list`;
    this.list.setAttribute('role', 'listbox');
    this.list.tabIndex = -1;
    this.list.hidden = true;

    this.wrap.append(this.button, this.list);
    select.classList.add('fb-select--native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    this.build();
    this.button.addEventListener('click', () => (this.open ? this.close() : this.show()));
    this.button.addEventListener('keydown', (e) => this.onButtonKey(e));
    this.list.addEventListener('keydown', (e) => this.onListKey(e));
    this.list.addEventListener('click', (e) => {
      const li = e.target.closest('[role="option"]');
      if (li && li.getAttribute('aria-disabled') !== 'true') this.choose(li.dataset.value, true);
    });
    document.addEventListener('pointerdown', (e) => {
      if (this.open && !this.wrap.contains(e.target)) this.close();
    });
    select.addEventListener('change', () => this.sync());
    new MutationObserver(() => this.build()).observe(select, { childList: true, subtree: true, attributes: true });
    this.typed = '';
  }

  findLabel() {
    const lab = this.select.closest('label');
    const span = lab ? lab.querySelector('span') : null;
    return span ? span.textContent.trim() : '';
  }

  build() {
    this.list.innerHTML = '';
    [...this.select.options].forEach((o) => {
      const li = document.createElement('li');
      li.className = 'fb-dd__opt';
      li.setAttribute('role', 'option');
      li.id = `${this.id}-o-${o.index}`;
      li.dataset.value = o.value;
      li.textContent = o.textContent;
      if (o.disabled) li.setAttribute('aria-disabled', 'true');
      if (o.dataset.meta) {
        const m = document.createElement('small');
        m.textContent = o.dataset.meta;
        li.appendChild(m);
      }
      this.list.appendChild(li);
    });
    this.sync();
  }

  sync() {
    const o = this.select.selectedOptions[0];
    this.value.textContent = o ? o.textContent : '';
    this.list.querySelectorAll('[role="option"]').forEach((li) => {
      const on = o && li.dataset.value === o.value;
      li.setAttribute('aria-selected', on ? 'true' : 'false');
      li.classList.toggle('is-selected', !!on);
    });
  }

  show() {
    if (this.select.disabled) return;
    this.open = true;
    this.list.hidden = false;
    this.wrap.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
    // Flip upwards when there is no room below.
    const r = this.list.getBoundingClientRect();
    this.wrap.classList.toggle('is-up', r.bottom > window.innerHeight - 8 && r.height < r.top);
    const cur = this.list.querySelector('.is-selected') || this.list.querySelector('[role="option"]');
    this.focusOpt(cur);
  }

  close(refocus = true) {
    if (!this.open) return;
    this.open = false;
    this.list.hidden = true;
    this.wrap.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    if (refocus) this.button.focus();
  }

  focusOpt(li) {
    if (!li) return;
    this.list.querySelectorAll('.is-focus').forEach((x) => x.classList.remove('is-focus'));
    li.classList.add('is-focus');
    this.list.setAttribute('aria-activedescendant', li.id);
    this.list.focus({ preventScroll: true });
    li.scrollIntoView({ block: 'nearest' });
  }

  move(delta) {
    const opts = [...this.list.querySelectorAll('[role="option"]:not([aria-disabled="true"])')];
    if (!opts.length) return;
    const cur = this.list.querySelector('.is-focus');
    let i = Math.max(0, opts.indexOf(cur));
    i = (i + delta + opts.length) % opts.length;
    this.focusOpt(opts[i]);
  }

  choose(value, close) {
    if (this.select.value !== value) {
      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.sync();
    if (close) this.close();
  }

  onButtonKey(e) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      this.show();
    }
  }

  onListKey(e) {
    const cur = this.list.querySelector('.is-focus');
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.move(-1);
        break;
      case 'Home':
        e.preventDefault();
        this.focusOpt(this.list.querySelector('[role="option"]:not([aria-disabled="true"])'));
        break;
      case 'End': {
        e.preventDefault();
        const all = this.list.querySelectorAll('[role="option"]:not([aria-disabled="true"])');
        this.focusOpt(all[all.length - 1]);
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (cur) this.choose(cur.dataset.value, true);
        break;
      case 'Escape':
        e.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close(false);
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          this.typed += e.key.toLowerCase();
          clearTimeout(this.typedTimer);
          this.typedTimer = setTimeout(() => (this.typed = ''), 600);
          const hit = [...this.list.querySelectorAll('[role="option"]')].find((li) => li.textContent.toLowerCase().startsWith(this.typed));
          if (hit) this.focusOpt(hit);
        }
    }
  }
}

const boot = (root = document) => {
  root.querySelectorAll('select.fb-select:not(.fb-select--native)').forEach((s) => {
    if (!s.__fbdd) s.__fbdd = new FbSelect(s);
  });
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
else boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
